// components/footfall/PulseDashboard.tsx
'use client';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Copy, Map as MapIcon, Pencil, Trash2 } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { InventoryListToolbar } from '@/components/portal/InventoryListToolbar';
import { PULSE_PILOT_PROPERTY_ID, venueLevelPaths, venuePlaces } from '#lib/footfall/pulse';
import type { PulsePlan } from '#types/types';
import { createPlan, deletePlan, hydrateCentre, openPlan, planNameError, renamePlan } from './pulseState';
import { campaignOption, useCentreCampaigns } from './PlanScenarios';

const t = (fontSize: number) => ({ fontSize });
const focus = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8AAAEE]';
const SORT_OPTIONS = [{ label: 'Last edited', field: 'UPDATED' }, { label: 'Name', field: 'NAME' }];
const noop = () => {};
const EMPTY: Record<string, unknown> = {};
const keep = (f: Record<string, unknown>) => f;
const unlocked = () => false;
const iconButton = `flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white ${focus}`;

/** The plan's ground level drawn as its shops, coloured by trade, with planned moves outlined: the card's picture. */
const PlanThumbnail = memo(function PlanThumbnail({ plan }: { plan: PulsePlan }) {
  const property = useKernelStore(s => s.inventory.Property.registry[plan.propertyId]);
  const brands = useKernelStore(s => s.inventory.Brand.registry);
  const venue = property?.venue ?? null;
  const shapes = useMemo(() => {
    if (!venue) return null;
    const level = venue.levels.find(l => l.id === plan.levelId) ?? venue.levels.find(l => l.ordinal === 0) ?? venue.levels[0];
    const moved = new Set(plan.moves.map(m => m.shopId));
    return venueLevelPaths(venue, venuePlaces(venue, brands, property?.footfall?.shops ?? []), level?.id ?? null)?.map(s => ({ ...s, moved: moved.has(s.id) })) ?? null;
  }, [venue, plan.levelId, plan.moves, property, brands]);
  if (!shapes) return <div className="absolute inset-0 flex items-center justify-center"><MapIcon size={28} color="#3A3A3A" /></div>;
  return <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden>
    {shapes.map(s => <path key={s.id} d={s.d} fill={s.fill} fillOpacity={0.85} stroke={s.moved ? '#F0F0F0' : '#000000'} strokeWidth={s.moved ? 1.6 : 0.5} strokeDasharray={s.moved ? '3 2' : undefined} />)}
  </svg>;
});

const PlanCard = memo(function PlanCard({ plan, centre, onOpen, onRename, onDuplicate, onDelete }: {
  plan: PulsePlan; centre: string; onOpen: (id: string) => void; onRename: (id: string, name: string) => Promise<string | null>; onDuplicate: (plan: PulsePlan) => void; onDelete: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const moves = plan.moves.length;
  const commit = async (name: string) => {
    if (name.trim() === plan.name) { setRenaming(false); setError(null); return; }
    const refusal = await onRename(plan.id, name);
    setError(refusal);
    if (!refusal) setRenaming(false);
  };
  return <div className="group relative cursor-pointer" data-testid="pulse-plan-card" onClick={() => !renaming && onOpen(plan.id)}>
    <div className="relative aspect-[4/3] overflow-hidden rounded-[16px] bg-[#1B1B1B]"><PlanThumbnail plan={plan} />
      <div className={`absolute right-2 top-2 flex items-center rounded-full bg-[#2A2A2A]/90 transition-opacity ${confirming ? '' : 'opacity-0 focus-within:opacity-100 group-hover:opacity-100'}`} onClick={e => e.stopPropagation()} onMouseLeave={() => setConfirming(false)}>
        {confirming ? <>
          <button type="button" className={`h-8 rounded-full px-3 text-[#F28B82] transition-colors hover:bg-[#F28B82]/10 ${focus}`} style={t(13)} onClick={() => { setConfirming(false); onDelete(plan.id); }}>Delete</button>
          <button type="button" className={`h-8 rounded-full px-3 text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white ${focus}`} style={t(13)} onClick={() => setConfirming(false)}>Cancel</button>
        </> : <div className="flex">
          <button type="button" title="Rename" aria-label={`Rename ${plan.name}`} className={iconButton} onClick={() => setRenaming(true)}><Pencil size={15} /></button>
          <button type="button" title="Duplicate" aria-label={`Duplicate ${plan.name}`} className={iconButton} onClick={() => onDuplicate(plan)}><Copy size={15} /></button>
          <button type="button" title="Delete" aria-label={`Delete ${plan.name}`} className={iconButton} onClick={() => setConfirming(true)}><Trash2 size={15} /></button>
        </div>}
      </div>
    </div>
    <div className="min-w-0 pl-1 pt-2.5">
        {renaming ? <input autoFocus aria-label="Plan name" defaultValue={plan.name} maxLength={80} className={`h-6 w-full rounded-[6px] bg-[#1F1F1F] px-1.5 text-[#ECECEC] focus:outline-none focus:ring-1 focus:ring-[#444444] ${error ? 'ring-1 ring-[#F28B82]' : ''}`} style={t(14)} onClick={e => e.stopPropagation()} onBlur={e => void commit(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void commit((e.target as HTMLInputElement).value); if (e.key === 'Escape') { setRenaming(false); setError(null); } }} />
          : <h3 className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }} title={plan.name}>{plan.name}</h3>}
        <p className={`truncate ${error ? 'text-[#F28B82]' : 'text-[#8E8E8E]'}`} style={{ fontSize: 13, lineHeight: '18px' }} role={error ? 'alert' : undefined}>
          {error ?? [centre, `${moves} move${moves === 1 ? '' : 's'}`, plan.savedAt ? `Edited ${formatDistanceToNow(new Date(plan.savedAt), { addSuffix: true })}` : ''].filter(Boolean).join(' · ')}
        </p>
    </div>
  </div>;
});

/** "New plan": one centre, one name. The centre list is the user's Source assets, the pilot centre first. */
function NewPlanDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { kernel } = useKernel();
  const assets = useKernelStore(s => s.sourceAssets);
  const properties = useKernelStore(s => s.inventory.Property.registry);
  const rows = useMemo(() => assets.ids.flatMap(id => properties[id] ? [properties[id]!] : []).sort((a, b) => Number(b.id === PULSE_PILOT_PROPERTY_ID) - Number(a.id === PULSE_PILOT_PROPERTY_ID) || a.name.localeCompare(b.name)), [assets.ids, properties]);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const chosen = propertyId ?? rows[0]?.id ?? null;
  const runs = useCentreCampaigns(chosen);
  const run = campaignId && runs.campaigns.some(c => c.id === campaignId) ? campaignId : runs.campaigns[0]?.id ?? null;
  const submit = async () => {
    const k = kernel.current;
    if (!k || !chosen || !run || busy) return;
    const refusal = planNameError(name);
    if (refusal) { setError(refusal); return; }
    setBusy(true);
    const result = await createPlan(k, chosen, name, undefined, run);
    setBusy(false);
    if ('error' in result) setError(result.error); else onCreated(result.id);
  };
  return <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
    <form role="dialog" aria-modal="true" aria-labelledby="pulse-new-plan" className="w-full max-w-[420px] rounded-[24px] bg-[#212121] p-6 text-[#ECECEC] shadow-[0_16px_48px_rgba(0,0,0,0.5)]" onSubmit={e => { e.preventDefault(); void submit(); }}>
      <h2 id="pulse-new-plan" className="font-semibold" style={t(18)}>New plan</h2>
      <label className="mt-5 block"><span className="mb-1.5 block text-[#AFAFAF]" style={t(13)}>Centre</span>
        {assets.status === 'ready' && rows.length === 0 ? <p className="text-[#8E8E8E]" style={t(13)}>No centre is open to this account.</p>
          : <select aria-label="Centre" className="h-10 w-full rounded-[12px] border border-[#2A2A2A] bg-[#1B1B1B] px-3 text-[#ECECEC] focus:border-[#444444] focus:outline-none" style={t(14)} value={chosen ?? ''} disabled={assets.status !== 'ready'} onChange={e => setPropertyId(e.target.value)}>
            {assets.status !== 'ready' && <option>Loading centres</option>}
            {rows.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>}
      </label>
      <label className="mt-4 block"><span className="mb-1.5 block text-[#AFAFAF]" style={t(13)}>Shortlist</span>
        {runs.status === 'ready' && !runs.campaigns.length ? <p className="text-[#8E8E8E]" style={t(13)}>This centre has no Source run yet. Run one in Source first: a plan places brands from a shortlist.</p>
          : <select aria-label="Shortlist" className="h-10 w-full rounded-[12px] border border-[#2A2A2A] bg-[#1B1B1B] px-3 text-[#ECECEC] focus:border-[#444444] focus:outline-none" style={t(14)} value={run ?? ''} disabled={runs.status !== 'ready'} onChange={e => setCampaignId(e.target.value)}>
            {runs.status !== 'ready' && <option>{runs.status === 'error' ? 'Could not load the Source runs' : 'Loading Source runs'}</option>}
            {runs.campaigns.map(c => <option key={c.id} value={c.id}>{campaignOption(c, false)}</option>)}
          </select>}
      </label>
      <label className="mt-4 block"><span className="mb-1.5 block text-[#AFAFAF]" style={t(13)}>Name</span>
        <input autoFocus aria-label="Plan name" maxLength={80} placeholder="Repositioning 2027" value={name} onChange={e => { setName(e.target.value); setError(null); }} className={`h-10 w-full rounded-[12px] border border-[#2A2A2A] bg-[#1B1B1B] px-3 text-[#ECECEC] placeholder:text-[#6E6E6E] focus:border-[#444444] focus:outline-none ${error ? 'border-[#F28B82]' : ''}`} style={t(14)} />
      </label>
      {error && <p role="alert" className="mt-2 text-[#F28B82]" style={t(13)}>{error}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className={`h-9 rounded-full px-4 text-[#ECECEC] transition-colors hover:bg-white/[0.08] ${focus}`} style={t(14)} onClick={onClose}>Cancel</button>
        <button type="submit" disabled={!chosen || !run || busy} className={`h-9 rounded-full bg-[#F0F0F0] px-4 font-medium text-black transition-colors hover:bg-white disabled:opacity-40 ${focus}`} style={t(14)}>{busy ? 'Creating' : 'Create'}</button>
      </div>
    </form>
  </div>;
}

export function PulseDashboard() {
  const { kernel } = useKernel();
  const user = useKernelStore(s => s.userContext);
  const preferencesOwner = useKernelStore(s => s.userPreferencesOwnerId);
  const plansMap = useKernelStore(s => s.userPreferences.pulsePlans);
  const properties = useKernelStore(s => s.inventory.Property.registry);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('UPDATED');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = !!user.dbUserId && preferencesOwner === user.dbUserId;
  const plans = useMemo(() => Object.values(plansMap ?? {}), [plansMap]);
  const centreKey = [...new Set(plans.map(p => p.propertyId))].sort().join(',');

  useEffect(() => { void kernel.current?.loadSourceAssets(); }, [kernel]);
  // The cards draw each centre's plan: hydrate every centre a plan sits on, once per visit.
  useEffect(() => {
    const k = kernel.current;
    if (k) for (const id of centreKey ? centreKey.split(',') : []) void hydrateCentre(k, id);
  }, [kernel, centreKey]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const dir = sortDir === 'asc' ? 1 : -1;
    return plans.filter(p => !q || p.name.toLowerCase().includes(q) || (properties[p.propertyId]?.name ?? '').toLowerCase().includes(q))
      .sort((a, b) => (sortField === 'NAME' ? a.name.localeCompare(b.name) : a.savedAt.localeCompare(b.savedAt)) * dir);
  }, [plans, properties, search, sortField, sortDir]);

  const onSortChange = useCallback((field: string) => {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir(field === 'NAME' ? 'asc' : 'desc'); }
  }, [sortField]);
  const open = useCallback((id: string) => {
    if (kernel.current) openPlan(kernel.current, id);
  }, [kernel]);
  const rename = useCallback(async (id: string, name: string) => kernel.current ? renamePlan(kernel.current, id, name) : null, [kernel]);
  const duplicate = useCallback(async (plan: PulsePlan) => {
    const k = kernel.current;
    if (!k) return;
    const result = await createPlan(k, plan.propertyId, `${plan.name} (copy)`.slice(0, 80), plan);
    setError('error' in result ? result.error : null);
  }, [kernel]);
  const remove = useCallback(async (id: string) => {
    const k = kernel.current;
    if (k) setError(await deletePlan(k, id));
  }, [kernel]);

  return <div className="relative flex h-full w-full flex-col bg-black" data-testid="footfall-module">
    <div className="shrink-0">
      <InventoryListToolbar title="Plan" onCreateClick={() => setCreating(true)} createButtonLabel="New plan" searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search plans"
        disableManualRefresh refreshIsLoading={false} onManualRefresh={noop} filterOptions={[]} viewFilters={EMPTY} isFilterKeyLocked={unlocked} mergeWithLockedFilters={keep} setFilters={noop}
        sortOptions={SORT_OPTIONS} activeSortField={sortField} activeSortDir={sortDir} onSortChange={onSortChange} currentSortLabel={SORT_OPTIONS.find(o => o.field === sortField)?.label ?? 'Sort'} />
    </div>
    <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pb-6">
      {error && <p role="alert" className="pb-4 text-[#F28B82]" style={t(13)}>{error}</p>}
      {!ready ? <p className="p-12 text-center text-[#8E8E8E]" role="status" style={t(14)}>Loading your plans</p>
        : plans.length === 0 ? <div className="mx-auto flex max-w-[440px] flex-col items-center gap-3 p-12 text-center">
          <MapIcon size={28} color="#5A5A5A" />
          <p className="font-medium text-[#ECECEC]" style={t(16)}>No merchandising plan yet</p>
          <p className="text-[#8E8E8E]" style={t(14)}>A plan stages tenant moves on a centre&apos;s map, prices them from the rent roll and footfall, and feeds the asset&apos;s business plan.</p>
          <button type="button" className={`mt-2 h-9 rounded-full bg-[#F0F0F0] px-4 font-medium text-black transition-colors hover:bg-white ${focus}`} style={t(14)} onClick={() => setCreating(true)}>New plan</button>
        </div>
        : list.length === 0 ? <p className="p-12 text-center text-[#8E8E8E]" style={t(14)}>No plan matches &quot;{search}&quot;.</p>
        : <div className="grid grid-cols-2 gap-4 gap-y-7 lg:grid-cols-3 xl:grid-cols-4">
          {list.map(plan => <PlanCard key={plan.id} plan={plan} centre={properties[plan.propertyId]?.name ?? ''} onOpen={open} onRename={rename} onDuplicate={duplicate} onDelete={id => void remove(id)} />)}
        </div>}
    </div>
    {creating && <NewPlanDialog onClose={() => setCreating(false)} onCreated={id => { setCreating(false); open(id); }} />}
  </div>;
}
